import { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState({ name: '', email: '' });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const res = await apiFetch('/api/users/profile');
        if (!res.ok) {
          throw new Error('Failed to load profile');
        }
        const data = await res.json();
        setProfile(data);
        setFormState({ name: data.name, email: data.email });
      } catch (err) {
        setError(err.message || 'Unable to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const res = await apiFetch('/api/users/profile', {
        method: 'PUT',
        body: JSON.stringify(formState),
      });
      if (!res.ok) {
        throw new Error('Failed to update profile');
      }
      const data = await res.json();
      setProfile(data);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message || 'Unable to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600 shadow-sm">Loading profile...</div>;
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-900 text-lg font-semibold text-white">{profile?.name?.slice(0, 2).toUpperCase() || 'AD'}</div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{profile?.name || 'Admin User'}</h2>
            <p className="text-sm text-slate-500">{profile?.isAdmin ? 'Super Administrator' : 'Administrator'}</p>
          </div>
        </div>
        <div className="space-y-3 text-sm text-slate-600">
          <p><span className="font-semibold text-slate-900">Email:</span> {profile?.email}</p>
          <p><span className="font-semibold text-slate-900">Role:</span> {profile?.isAdmin ? 'Admin' : 'User'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Profile Settings</h2>
        {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {success && <div className="mb-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">{success}</div>}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm text-slate-600">
            <span className="mb-1 block font-medium text-slate-800">Full Name</span>
            <input name="name" className="w-full rounded-xl border border-slate-200 px-3 py-2" value={formState.name} onChange={handleChange} />
          </label>
          <label className="text-sm text-slate-600">
            <span className="mb-1 block font-medium text-slate-800">Email</span>
            <input name="email" className="w-full rounded-xl border border-slate-200 px-3 py-2" value={formState.email} onChange={handleChange} />
          </label>
        </div>
        <button type="submit" className="mt-5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}
